import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { fetchSapMonitoredRuns, fetchSapUpcomingJobs } from '@/lib/sapJobsApi';
import {
  DEFAULT_SAP_CLIENT,
  DEFAULT_SAP_SYSTEM,
  SAP_JOB_LIST_POLL_MS,
  SAP_JOB_MONITORED_PAGE_SIZE,
  SAP_JOB_UPCOMING_PAGE_SIZE,
  defaultFinishedExecutionDateRange,
  executionDateRangeParams,
} from '@/lib/sapJobsConfig';
import { formatDurationSeconds, formatIsoDateTime, MONITORING_STAGE_LABELS } from '@/lib/sapJobStatus';
import SapJobStatusBadge from '@/components/sapJobs/SapJobStatusBadge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

function jobDetailPath(monitorSlotId) {
  return `/sap-job-monitoring/jobs/${encodeURIComponent(monitorSlotId)}`;
}

function runDetailPath(runId) {
  return `/sap-job-monitoring/runs/${encodeURIComponent(runId)}`;
}

const DEFAULT_FINISHED_RANGE = defaultFinishedExecutionDateRange();

const MONITORED_BUCKETS = [
  { value: 'running', label: 'Running' },
  { value: 'finished', label: 'Finished' },
  { value: 'canceled', label: 'Canceled / Failed' },
];

export default function SapJobMonitoring() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canView = hasPermission('dashboard', 'read');

  const [sapSystem, setSapSystem] = useState(DEFAULT_SAP_SYSTEM);
  const [sapClient, setSapClient] = useState(DEFAULT_SAP_CLIENT);
  const [jobSearch, setJobSearch] = useState('');
  const [executionFromDate, setExecutionFromDate] = useState(DEFAULT_FINISHED_RANGE.from);
  const [executionToDate, setExecutionToDate] = useState(DEFAULT_FINISHED_RANGE.to);
  const [section, setSection] = useState('monitored');
  const [monitoredBucket, setMonitoredBucket] = useState('finished');
  const [monitoredOffset, setMonitoredOffset] = useState(0);
  const [monitoredTotal, setMonitoredTotal] = useState(null);
  const [monitoredHasMore, setMonitoredHasMore] = useState(false);

  const [monitoredRuns, setMonitoredRuns] = useState([]);
  const [upcomingJobs, setUpcomingJobs] = useState([]);
  const [upcomingOffset, setUpcomingOffset] = useState(0);
  const [upcomingHasMore, setUpcomingHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadMonitored = useCallback(async ({ silent = false } = {}) => {
    if (!canView) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const jobName = jobSearch.trim();
      const data = await fetchSapMonitoredRuns({
        sap_system: sapSystem.trim(),
        sap_client: sapClient.trim(),
        bucket: monitoredBucket,
        ...(jobName ? { job_name: jobName } : {}),
        ...executionDateRangeParams(executionFromDate, executionToDate),
        limit: SAP_JOB_MONITORED_PAGE_SIZE,
        offset: monitoredOffset,
      });
      setMonitoredRuns(Array.isArray(data?.runs) ? data.runs : []);
      setMonitoredTotal(typeof data?.total_count === 'number' ? data.total_count : null);
      setMonitoredHasMore(Boolean(data?.has_more));
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Request failed');
      if (!silent) setMonitoredRuns([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    canView,
    sapSystem,
    sapClient,
    monitoredBucket,
    jobSearch,
    executionFromDate,
    executionToDate,
    monitoredOffset,
  ]);

  const loadUpcoming = useCallback(async ({ silent = false } = {}) => {
    if (!canView) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const jobName = jobSearch.trim();
      const data = await fetchSapUpcomingJobs({
        sap_system: sapSystem.trim(),
        sap_client: sapClient.trim(),
        ...(jobName ? { job_name: jobName } : {}),
        limit: SAP_JOB_UPCOMING_PAGE_SIZE,
        offset: upcomingOffset,
      });
      setUpcomingJobs(Array.isArray(data?.items) ? data.items : []);
      setUpcomingHasMore(Boolean(data?.has_more));
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Request failed');
      if (!silent) setUpcomingJobs([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [canView, sapSystem, sapClient, jobSearch, upcomingOffset]);

  const load = useCallback(
    ({ silent = false } = {}) => {
      if (section === 'upcoming') {
        return loadUpcoming({ silent });
      }
      return loadMonitored({ silent });
    },
    [section, loadMonitored, loadUpcoming],
  );

  useEffect(() => {
    load();
  }, [load]);

  const resetMonitoredPagination = useCallback(() => {
    setMonitoredOffset(0);
  }, []);

  useEffect(() => {
    if (!canView) return undefined;
    const timer = setInterval(() => load({ silent: true }), SAP_JOB_LIST_POLL_MS);
    return () => clearInterval(timer);
  }, [canView, load]);

  if (!canView) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          You do not have permission to view SAP Job Monitoring (requires dashboard read).
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="sap-job-monitoring-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Clock className="h-7 w-7 text-cp-accent" />
            SAP Job Monitoring
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Read-only monitoring of SAP background jobs — local Job Failure data when configured.
          </p>
          {user?.email ? (
            <p className="text-xs text-muted-foreground mt-1" data-testid="sap-job-signed-in-as">
              Signed in as {user.email}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => load({ silent: true })}
          disabled={refreshing}
          data-testid="sap-job-refresh"
        >
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Scope</CardTitle>
          <CardDescription>SAP system and client for local Job Failure database reads.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="text-xs text-muted-foreground">SAP system</label>
            <Input
              value={sapSystem}
              onChange={(e) => {
                setSapSystem(e.target.value);
                resetMonitoredPagination();
                setUpcomingOffset(0);
              }}
              data-testid="filter-sap-system"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">SAP client</label>
            <Input
              value={sapClient}
              onChange={(e) => {
                setSapClient(e.target.value);
                resetMonitoredPagination();
                setUpcomingOffset(0);
              }}
              data-testid="filter-sap-client"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Job name search</label>
            <Input
              value={jobSearch}
              onChange={(e) => {
                setJobSearch(e.target.value);
                resetMonitoredPagination();
                setUpcomingOffset(0);
              }}
              placeholder="Contains…"
              data-testid="filter-job-search"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Execution from (UTC day)</label>
            <Input
              type="date"
              value={executionFromDate}
              onChange={(e) => {
                setExecutionFromDate(e.target.value);
                resetMonitoredPagination();
              }}
              data-testid="filter-execution-from"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Execution to (UTC day)</label>
            <Input
              type="date"
              value={executionToDate}
              onChange={(e) => {
                setExecutionToDate(e.target.value);
                resetMonitoredPagination();
              }}
              data-testid="filter-execution-to"
            />
          </div>
        </CardContent>
      </Card>

      <Tabs value={section} onValueChange={setSection}>
        <TabsList>
          <TabsTrigger value="monitored" data-testid="tab-monitored-runs">
            Monitored Runs
          </TabsTrigger>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming-jobs">
            Upcoming Jobs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="monitored" className="mt-4 space-y-4">
          <Tabs
            value={monitoredBucket}
            onValueChange={(value) => {
              setMonitoredBucket(value);
              resetMonitoredPagination();
            }}
          >
            <TabsList>
              {MONITORED_BUCKETS.map((b) => (
                <TabsTrigger key={b.value} value={b.value}>
                  {b.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monitored runs</CardTitle>
              <CardDescription>
                Actual executions (Running, Finished, Canceled) from HALO run history — not live SAP.
                {monitoredTotal != null
                  ? ` Page shows up to ${SAP_JOB_MONITORED_PAGE_SIZE} rows; use Next for more matches.`
                  : ''}
                {refreshing ? ' · updating…' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Loading runs…
                </div>
              ) : error ? (
                <div className="py-12 text-center" data-testid="sap-job-list-error">
                  <p className="text-destructive font-medium">Could not load monitored runs</p>
                  <p className="text-sm text-muted-foreground mt-2">{error}</p>
                  <Button className="mt-4" variant="outline" onClick={() => load()}>
                    Retry
                  </Button>
                </div>
              ) : monitoredRuns.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground" data-testid="sap-job-monitored-empty">
                  No monitored runs in this category.
                </div>
              ) : (
                <Table data-testid="sap-job-monitored-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Scheduler / created by</TableHead>
                      <TableHead>Execution user</TableHead>
                      <TableHead>Actual start</TableHead>
                      <TableHead>Runtime</TableHead>
                      <TableHead>Monitoring</TableHead>
                      <TableHead>Failure summary</TableHead>
                      <TableHead>Evidence</TableHead>
                      <TableHead>Ticket</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monitoredRuns.map((row) => (
                      <TableRow key={row.run_id}>
                        <TableCell className="font-medium">
                          <Link to={runDetailPath(row.run_id)} className="text-cp-accent hover:underline">
                            {row.job_name}
                          </Link>
                          {row.monitor_slot_id ? (
                            <div className="text-xs text-muted-foreground mt-0.5">
                              <Link to={jobDetailPath(row.monitor_slot_id)} className="hover:underline">
                                Job detail
                              </Link>
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <SapJobStatusBadge sapStatus={row.status} monitoringStage={row.monitoring_stage} />
                        </TableCell>
                        <TableCell className="text-sm">{row.scheduled_by || row.released_by || '—'}</TableCell>
                        <TableCell className="text-sm font-mono">{row.auth_user || '—'}</TableCell>
                        <TableCell>{formatIsoDateTime(row.actual_start)}</TableCell>
                        <TableCell>{formatDurationSeconds(row.duration_seconds)}</TableCell>
                        <TableCell className="text-xs">
                          {row.monitoring_stage
                            ? MONITORING_STAGE_LABELS[row.monitoring_stage] || row.monitoring_stage
                            : '—'}
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                          {row.failure_message_summary || '—'}
                        </TableCell>
                        <TableCell className="text-xs">
                          {row.evidence_status ? (
                            <Link to={runDetailPath(row.run_id)} className="text-cp-accent hover:underline">
                              View
                            </Link>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-xs">
                          {row.halo_ticket_number ? (
                            <Link
                              to={`/tickets/${encodeURIComponent(row.halo_ticket_number)}`}
                              className="text-cp-accent hover:underline"
                            >
                              {row.halo_ticket_number}
                            </Link>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {!loading && !error && monitoredRuns.length > 0 ? (
                <div
                  className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t"
                  data-testid="sap-job-monitored-pagination"
                >
                  <p className="text-xs text-muted-foreground">
                    {monitoredTotal != null
                      ? `Showing ${monitoredOffset + 1}–${monitoredOffset + monitoredRuns.length} of ${monitoredTotal}`
                      : `Showing ${monitoredRuns.length} runs`}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={monitoredOffset <= 0}
                      onClick={() => setMonitoredOffset((o) => Math.max(0, o - SAP_JOB_MONITORED_PAGE_SIZE))}
                      data-testid="sap-job-monitored-prev"
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!monitoredHasMore}
                      onClick={() => setMonitoredOffset((o) => o + SAP_JOB_MONITORED_PAGE_SIZE)}
                      data-testid="sap-job-monitored-next"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="upcoming" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Upcoming jobs</CardTitle>
              <CardDescription>
                Released (S) jobs in the schedule window, including delayed slots whose planned start has passed
                (warning only — not a failure).
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground">Loading…</div>
              ) : error ? (
                <div className="py-12 text-center" data-testid="sap-job-list-error">
                  <p className="text-destructive font-medium">Could not load upcoming jobs</p>
                  <p className="text-sm text-muted-foreground mt-2">{error}</p>
                </div>
              ) : upcomingJobs.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground" data-testid="sap-job-upcoming-empty">
                  No upcoming released jobs in the next 48 hours.
                </div>
              ) : (
                <Table data-testid="sap-job-upcoming-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job name</TableHead>
                      <TableHead>Planned start</TableHead>
                      <TableHead>Profile samples</TableHead>
                      <TableHead>Expected runtime</TableHead>
                      <TableHead>P90 runtime</TableHead>
                      <TableHead>Expected finish</TableHead>
                      <TableHead>Next check</TableHead>
                      <TableHead>Stage / warning</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {upcomingJobs.map((row) => (
                      <TableRow key={row.monitor_slot_id}>
                        <TableCell className="font-medium">
                          <Link to={jobDetailPath(row.monitor_slot_id)} className="text-cp-accent hover:underline">
                            {row.job_name}
                          </Link>
                        </TableCell>
                        <TableCell>{formatIsoDateTime(row.planned_start)}</TableCell>
                        <TableCell>
                          {row.profile_valid ? row.profile_sample_count : 'Not enough data yet'}
                        </TableCell>
                        <TableCell>{formatDurationSeconds(row.median_runtime_seconds)}</TableCell>
                        <TableCell>{formatDurationSeconds(row.p90_runtime_seconds)}</TableCell>
                        <TableCell>{formatIsoDateTime(row.expected_finish_at)}</TableCell>
                        <TableCell>{formatIsoDateTime(row.next_check_at)}</TableCell>
                        <TableCell>
                          {row.monitoring_stage ? (
                            <SapJobStatusBadge sapStatus="S" monitoringStage={row.monitoring_stage} />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {!loading && !error && (upcomingJobs.length > 0 || upcomingOffset > 0) ? (
                <div
                  className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-4 border-t"
                  data-testid="sap-job-upcoming-pagination"
                >
                  <p className="text-xs text-muted-foreground">
                    {`Showing ${upcomingOffset + 1}–${upcomingOffset + upcomingJobs.length}`}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={upcomingOffset <= 0}
                      onClick={() =>
                        setUpcomingOffset((current) => Math.max(0, current - SAP_JOB_UPCOMING_PAGE_SIZE))
                      }
                      data-testid="sap-job-upcoming-prev"
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!upcomingHasMore}
                      onClick={() => setUpcomingOffset((current) => current + SAP_JOB_UPCOMING_PAGE_SIZE)}
                      data-testid="sap-job-upcoming-next"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
